/* ✦ POLISH IT (her call, 2026-11-04) — the pop-up that asks how much help, and
 * the screen the polished version waits on: Brill's notes as a chat on the left,
 * the version on the right with every change marked. Pressing a mark shows her
 * own words and puts them back. Nothing reaches her draft until she presses Use
 * this version; until then it waits, kept, and comes back if she leaves.
 * The logic lives in utils/newsletterPolish.js; this file only draws it.
 */
import { useRef, useState } from "react";
import { B, btnGhost, btnPrimary, input } from "./tokens";
import { ChatPane } from "./ui";
import { PaneToggle } from "./shell";
import { POLISH_LEVELS, changeCount, editHunk, polishDiff, revertHunk } from "../../utils/newsletterPolish";
import { titleOption } from "../../utils/newsletterShape";

const words = (s) => (String(s || "").match(/\S+/g) || []).length;
const head = { margin: "14px 0 6px", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: B.mid, fontWeight: 600 };

/* ── The pop-up ─────────────────────────────────────────────────────────── */
export function PolishSetup({ opts, setOpts, offers = [], offerId, setOfferId, onGo, onClose }) {
  const put = (k, v) => setOpts((o) => ({ ...o, [k]: v }));
  const live = offers.filter((o) => o.status !== "retired");
  const tick = (k, label, hint, extra = {}) => (
    <label style={{ display: "flex", gap: 10, padding: "8px 0", cursor: extra.disabled ? "default" : "pointer", alignItems: "flex-start" }}>
      <input type="checkbox" checked={extra.disabled ? true : !!opts[k]} disabled={extra.disabled}
        onChange={() => put(k, !opts[k])} style={{ marginTop: 3, accentColor: "var(--accent)" }} />
      <span>
        <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: B.text }}>{label}</span>
        <span style={{ display: "block", fontSize: 12, color: B.dim, lineHeight: 1.5 }}>{hint}</span>
      </span>
    </label>
  );
  return (
    <div onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Polish it" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 20px 16px", width: "100%", maxWidth: 480, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 10 }}>✦ Polish it</div>

        <div style={head}>How much help</div>
        {POLISH_LEVELS.map((l) => (
          <label key={l.id} style={{ display: "flex", gap: 10, padding: "7px 0", cursor: "pointer", alignItems: "flex-start" }}>
            <input type="radio" name="polish-level" checked={opts.level === l.id} onChange={() => put("level", l.id)} style={{ marginTop: 3, accentColor: "var(--accent)" }} />
            <span>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: B.text }}>{l.label}</span>
              <span style={{ display: "block", fontSize: 12, color: B.dim, lineHeight: 1.5 }}>{l.hint}</span>
            </span>
          </label>
        ))}

        <div style={head}>What to do</div>
        {tick("fix", "Fix spelling and grammar", "Always on.", { disabled: true })}
        {tick("tighten", "Tighten", "Cuts what doesn't earn its place. Your asides and jokes stay.")}
        {tick("search", "Make it search friendly", "A title, subheads above your paragraphs, and a short answer built from your words.")}
        {tick("offer", "Weave in my offer", "One or two sentences about one offer, and a next step at the end.")}
        {opts.offer && (
          live.length
            ? <select value={offerId} onChange={(e) => setOfferId(e.target.value)} aria-label="Which offer" style={{ ...input, margin: "0 0 6px 26px", width: "calc(100% - 26px)" }}>
                {live.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
              </select>
            : <div style={{ fontSize: 12, color: B.amber, margin: "0 0 6px 26px" }}>You have no offers on the Offer Ladder yet, so this will be skipped.</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "8px 14px", fontSize: 12.5 }}>Cancel</button>
          <button onClick={onGo} style={{ ...btnPrimary, padding: "8px 16px", fontSize: 12.5 }}>Polish it</button>
        </div>
      </div>
    </div>
  );
}

/* ── The version, with every change marked ──────────────────────────────── */
/* TYPE ANYWHERE (her ask, 2026-11-04: "the functionality of the title that we
   can change the text in place is the same we will want for the text"). The
   version is one editable block; what she types is read back when she leaves
   it, and the marks are drawn again from the new text. What is not the version
   (a cut she can still see, the ↺ buttons, an open card) is marked data-skip
   and never read back. Remounted on every new version (`key`), so React never
   draws over text she typed. */
export function readVersion(el) {
  let out = "";
  const walk = (node) => {
    for (const n of node.childNodes) {
      if (n.nodeType === 3) out += n.nodeValue;
      else if (n.nodeType === 1) {
        if (n.dataset && n.dataset.skip !== undefined) continue;
        if (n.tagName === "BR") { out += "\n"; continue; }
        /* A line the browser wrapped in its own <div> when she pressed Enter. */
        if (n.tagName === "DIV" && out && !out.endsWith("\n")) out += "\n";
        walk(n);
      }
    }
  };
  if (el) walk(el);
  return out;
}

function MarkedVersion({ original, version, setVersion, open, setOpen }) {
  const ref = useRef(null);
  const segs = polishDiff(original, version);
  const commit = (e) => {
    if (e && e.target !== ref.current) return;   // leaving a card's box is the card's business
    const typed = readVersion(ref.current);
    if (typed !== version) setVersion(typed);
  };
  const skip = { contentEditable: false, "data-skip": "" };
  return (
    <div ref={ref} key={version} contentEditable suppressContentEditableWarning
      role="textbox" aria-multiline="true" aria-label="Brill's version, editable"
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.execCommand("insertText", false, "\n"); } }}
      onPaste={(e) => { e.preventDefault(); document.execCommand("insertText", false, e.clipboardData.getData("text/plain")); }}
      style={{ fontFamily: "Lora, Georgia, serif", fontSize: 16.5, lineHeight: 1.8, color: B.text, whiteSpace: "pre-wrap", overflowWrap: "anywhere", outline: "none", cursor: "text", minHeight: 120 }}>
      {segs.map((s, i) => {
        if (s.same) return <span key={i}>{s.text}</span>;
        if (s.space) return <span key={i}>{s.now}</span>;
        const isOpen = open === i;
        const cutOnly = !s.now.trim();
        const opener = (
          <span {...skip} role="button" tabIndex={0} aria-expanded={isOpen} title="See your words"
            onClick={() => setOpen(isOpen ? null : i)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(isOpen ? null : i); } }}
            style={{ fontFamily: "system-ui, sans-serif", fontSize: 11, color: B.teal, border: `1px solid ${B.tealLine}`, borderRadius: 9, padding: "0 6px", margin: "0 3px", cursor: "pointer", userSelect: "none", verticalAlign: "1px" }}>
            {cutOnly ? "cut ↺" : "↺"}
          </span>
        );
        return (
          <span key={i}>
            {cutOnly
              ? <span {...skip} style={{ color: B.faint, textDecoration: "line-through", fontSize: 14 }}>{s.was.trim()}</span>
              : <span style={{ background: B.tealQuiet, borderBottom: `2px solid ${B.teal}`, borderRadius: 3 }}>{s.now}</span>}
            {opener}
            {cutOnly && <span {...skip}>{s.was.match(/\s*$/)[0] || " "}</span>}
            {isOpen && (
              <span {...skip}>
                <HunkCard key={i} seg={s} cutOnly={cutOnly}
                  onRevert={() => { setVersion(revertHunk(original, version, i)); setOpen(null); }}
                  onEdit={(typed) => { setVersion(editHunk(original, version, i, typed)); setOpen(null); }}
                  onClose={() => setOpen(null)} />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/* One change, opened: her words, and Brill's in a box she can type in (her
   ask, 2026-11-04: "you should be able to just type and edit the corrections
   as you go through them"). What she types goes in when she leaves the box or
   presses Use my edit, so moving on to the next change never loses it. The
   buttons keep the focus, so pressing one is not also leaving the box. */
function HunkCard({ seg, cutOnly, onRevert, onEdit, onClose }) {
  const start = cutOnly ? seg.was.trim() : seg.now.trim();
  const [typed, setTyped] = useState(start);
  const changed = typed.trim() !== start;
  const label = { display: "block", fontFamily: "system-ui, sans-serif", fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: B.mid, fontWeight: 600, marginBottom: 4 };
  const hold = (e) => e.preventDefault();
  return (
    <span style={{ display: "block", margin: "4px 0 10px", padding: "9px 11px", background: B.surface2, border: `1px solid ${B.border}`, borderRadius: 8, fontFamily: "inherit", fontSize: 13.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
      {seg.was.trim() && (<>
        <span style={label}>{cutOnly ? "Brill cut this" : "Your words"}</span>
        <span style={{ display: "block", marginBottom: 8 }}>{seg.was.trim()}</span>
      </>)}
      <span style={label}>{cutOnly ? "Keep some of it? Type here" : "Brill's, edit as you like"}</span>
      <textarea value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Edit this change"
        rows={Math.min(8, Math.max(2, Math.ceil(typed.length / 70)))}
        onBlur={() => { if (changed) onEdit(typed); }}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onEdit(typed); } }}
        style={{ ...input, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "Lora, Georgia, serif", fontSize: 14.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }} />
      <span style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", fontFamily: "system-ui, sans-serif" }}>
        {changed && (
          <button onMouseDown={hold} onClick={() => onEdit(typed)} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}>Use my edit</button>
        )}
        <button onMouseDown={hold} onClick={onRevert} style={{ ...btnGhost, padding: "4px 10px", fontSize: 12 }}>
          {seg.was.trim() ? "Put mine back" : "Take it out"}
        </button>
        <button onMouseDown={hold} onClick={onClose} style={{ ...btnGhost, padding: "4px 10px", fontSize: 12 }}>
          {cutOnly ? "Leave it cut" : "Keep Brill's"}
        </button>
      </span>
    </span>
  );
}

/* What goes around her words: picked here, put in on Use this version. */
function Extras({ extras, picks, setPicks }) {
  const x = extras || {};
  const flip = (k) => setPicks((p) => ({ ...p, [k]: !p[k] }));
  if (!x.answer && !x.claims?.length && !x.faq?.length && !x.next && !x.pillar) return null;
  return (
    <div style={{ marginTop: 26, paddingTop: 12, borderTop: `1px solid ${B.border}`, fontSize: 13 }}>
      {x.answer && (<>
        <div style={head}>The short answer, at the top of the blog page</div>
        <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={!!picks.answer} onChange={() => flip("answer")} />
          <span style={{ fontFamily: "Lora, Georgia, serif", lineHeight: 1.6 }}>{x.answer}</span>
        </label>
      </>)}
      {x.claims?.length > 0 && (<>
        <div style={head}>What this says</div>
        {x.claims.map((c) => (
          <label key={c.id} style={{ display: "flex", gap: 8, padding: "3px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={!!picks[c.id]} onChange={() => flip(c.id)} /><span>{c.text}</span>
          </label>
        ))}
      </>)}
      {x.faq?.length > 0 && (<>
        <div style={head}>Questions people ask</div>
        {x.faq.map((f) => (
          <label key={f.id} style={{ display: "flex", gap: 8, padding: "3px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={!!picks[f.id]} onChange={() => flip(f.id)} />
            <span>{f.q}<small style={{ display: "block", color: B.faint }}>{f.a}</small></span>
          </label>
        ))}
      </>)}
      {x.next && (<>
        <div style={head}>The next step, at the end</div>
        <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={!!picks.next} onChange={() => flip("next")} /><span>{x.next}</span>
        </label>
      </>)}
      {x.pillar && (
        <label style={{ display: "flex", gap: 8, padding: "10px 0 0", cursor: "pointer" }}>
          <input type="checkbox" checked={!!picks.pillar} onChange={() => flip("pillar")} />Pillar: {x.pillar}
        </label>
      )}
    </div>
  );
}

/* ── The screen ─────────────────────────────────────────────────────────── */
export function PolishScreen({
  original, version, setVersion, extras, picks, setPicks, turns, working, stage, problem,
  draftChanged, currentTitle, currentSubtitle = "", isMobile, onSend, onUse, onBack, titleCard = [], onDismissTitles,
}) {
  const [open, setOpen] = useState(null);
  const [chatText, setChatText] = useState("");
  const [pane, setPane] = useState("fields");
  /* Ready: a polish has come back, or this is Ask Brill's chat on her piece
     as it is (which may be empty). */
  const ready = Boolean(version) || (!working && !problem && turns.length > 0);
  const n = version ? changeCount(original, version) : 0;
  /* THE TITLE IS ON THIS SCREEN TOO (her read, 2026-11-04: "the title isn't in
     that text field"). She can type it here, or ask Brill and pick from a card. */
  const shownTitle = picks.title ?? currentTitle;
  const titleChanged = String(shownTitle).trim() !== String(currentTitle).trim();
  const setTitle = (t) => setPicks((p) => ({ ...p, title: t }));
  const shownSubtitle = picks.subtitle ?? currentSubtitle;
  const subtitleChanged = String(shownSubtitle).trim() !== String(currentSubtitle).trim();
  const nothingToUse = !ready || working || (version === original && !titleChanged && !subtitleChanged);
  const titleCardView = !working && titleCard.length > 0 && (
    <div style={{ background: B.surface, border: `1px solid ${B.tealLine}`, borderRadius: 12, padding: "10px 12px", margin: "2px 0 8px" }}>
      <div style={{ ...head, margin: "0 0 6px" }}>Titles</div>
      {titleCard.map(titleOption).map((t) => (
        <div key={t.title + t.subtitle} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 0", borderTop: `1px solid ${B.border}`, fontSize: 13 }}>
          <span style={{ flex: 1 }}>{t.title}{t.subtitle && <small style={{ display: "block", color: B.dim }}>{t.subtitle}</small>}</span>
          {String(shownTitle).trim() === t.title && (!t.subtitle || String(shownSubtitle).trim() === t.subtitle)
            ? <span style={{ fontSize: 11.5, color: B.teal }}>In</span>
            : <button onClick={() => setPicks((p) => ({ ...p, title: t.title, ...(t.subtitle ? { subtitle: t.subtitle } : {}) }))} style={{ ...btnGhost, padding: "3px 10px", fontSize: 12, whiteSpace: "nowrap" }}>{t.subtitle ? "Use both" : "Use this title"}</button>}
        </div>
      ))}
      <button onClick={onDismissTitles} style={{ border: "none", background: "none", color: B.dim, fontSize: 12, cursor: "pointer", padding: "6px 0 0", fontFamily: "inherit" }}>Not now</button>
    </div>
  );
  const send = (t) => { if (!String(t).trim() || working) return; setChatText(""); onSend(t.trim()); };

  const chat = (
    <ChatPane headless msgs={turns} sending={working && ready} chatText={chatText} setChatText={setChatText} onSend={send}
      placeholder="Tell Brill what to change…" height={isMobile ? "calc(100vh - 190px)" : "calc(100vh - 240px)"}
      pending={titleCardView}
      suggestions={!ready ? [] : version !== original ? ["Put my opening back", "Suggest a title", "Less formal"] : ["What's working in this?", "Is the ending strong enough?", "Suggest a title"]} />
  );
  const right = (
    <div style={{ flex: 1, minWidth: 0, border: `1px solid ${B.border}`, borderRadius: 14, background: B.surface, padding: isMobile ? "16px 14px" : "22px 26px", boxSizing: "border-box" }}>
      {!ready ? (
        <div style={{ fontSize: 13, color: problem ? B.amber : B.dim, padding: "20px 0" }}>{problem || stage || "Brill is polishing your piece…"}</div>
      ) : (<>
        {draftChanged && (
          <div style={{ fontSize: 12.5, color: B.amber, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 8, padding: "8px 11px", marginBottom: 12 }}>
            You've changed your draft since this was polished. Using this version replaces those changes.
          </div>
        )}
        {/* Textareas that grow, so a long title or subtitle wraps instead of
            running off the edge (her screenshot, 2026-11-04). */}
        <textarea rows={Math.max(1, Math.ceil(String(shownTitle).length / (isMobile ? 22 : 40)))} value={shownTitle} onChange={(e) => setTitle(e.target.value)} aria-label="Title" placeholder="Title this post"
          style={{ display: "block", fieldSizing: "content", resize: "none", overflow: "hidden", width: "100%", boxSizing: "border-box", border: "none", borderBottom: `1px dashed ${titleChanged ? B.teal : "transparent"}`, background: titleChanged ? B.tealQuiet : "transparent", outline: "none", padding: "2px 0", margin: 0, fontFamily: "inherit", fontSize: isMobile ? 22 : 26, fontWeight: 700, lineHeight: 1.3, color: B.text, borderRadius: 3 }} />
        <textarea rows={Math.max(1, Math.ceil(String(shownSubtitle).length / (isMobile ? 36 : 70)))} value={shownSubtitle} onChange={(e) => setPicks((p) => ({ ...p, subtitle: e.target.value }))} aria-label="Subtitle" placeholder="A line under the title (optional)"
          style={{ display: "block", fieldSizing: "content", resize: "none", overflow: "hidden", width: "100%", boxSizing: "border-box", border: "none", background: subtitleChanged ? B.tealQuiet : "transparent", outline: "none", padding: "2px 0", margin: "4px 0 0", fontFamily: "inherit", fontSize: 15.5, color: B.mid, borderRadius: 3 }} />
        <div style={{ fontSize: 11.5, color: B.faint, margin: "4px 0 16px", minHeight: 16 }}>
          {titleChanged && (<>New title. Yours was “{currentTitle || "untitled"}”. <button onClick={() => setTitle(currentTitle)} style={{ border: "none", background: "none", color: B.teal, cursor: "pointer", padding: 0, fontSize: 11.5, fontFamily: "inherit" }}>Put mine back</button></>)}
        </div>
        <MarkedVersion original={original} version={version} setVersion={setVersion} open={open} setOpen={setOpen} />
        <Extras extras={extras} picks={picks} setPicks={setPicks} />
      </>)}
    </div>
  );

  return (
    <div>
      {/* THE PAGE SCROLLS, not a box inside it (her read, 2026-11-04: "it isn't
          showing the rest of the post"). The version had its own scroll area,
          and a scrollbar that only shows while scrolling read as a post cut
          short. The bar and the chat stay put while the version scrolls. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "4px 0 12px", position: "sticky", top: -28, zIndex: 5, background: B.bg }}>
        <button onClick={onBack} style={{ border: "none", background: "none", color: B.mid, padding: "4px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
          ← Back to my draft
        </button>
        {ready && <span style={{ fontSize: 11.5, color: B.faint }}>{n ? `${n} ${n === 1 ? "change" : "changes"} · ${words(original)} → ${words(version)} words` : "No changes yet"}</span>}
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {isMobile && <PaneToggle pane={pane} setPane={setPane} otherLabel="Version" icons />}
          <button disabled={nothingToUse} onClick={onUse}
            style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12.5, opacity: nothingToUse ? 0.5 : 1 }}>
            Use this version
          </button>
        </span>
      </div>
      {isMobile
        ? (pane === "chat" ? chat : right)
        : <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}><div style={{ width: "38%", flex: "none", position: "sticky", top: 34 }}>{chat}</div>{right}</div>}
    </div>
  );
}
